import { useEffect, useMemo, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import { GameHud } from '@/components/GameHud';
import { GlassButton } from '@/components/GlassButton';
import { GlassCard } from '@/components/GlassCard';
import { Icon } from '@/components/Icon';
import { TapToAsk } from '@/components/TapToAsk';
import { sfx } from '@/lib/audio';
import { grade } from '@/lib/score';
import { ModeSwitcher } from './ModeSwitcher';
import { StatChip } from './StatChip';
import { StudyHeader } from './StudyHeader';
import { useStudyLesson } from './useStudyLesson';

/**
 * TypingPage v6 — listen + recall with skeleton + auto-skip punctuation.
 * Refactored to lean on useStudyLesson hook + shared components.
 */
export function TypingPage(): JSX.Element {
  const { ready, notFoundView, study } = useStudyLesson('typing');
  if (!ready || study === null) return notFoundView ?? <div />;

  const { course, lesson, sentence, index, total, progressPct, setProgressBoost,
          next, submitGrade, markComplete, bumpStreak, bumpMastery, recordMistake } = study;

  const [input, setInput] = useState('');
  const [startTime, setStartTime] = useState<number | null>(null);
  const [status, setStatus] = useState<'pending' | 'correct' | 'wrong'>('pending');
  // peekVisible — UI toggle (show/hide answer)
  // peekedOnce  — sticky penalty: once you peeked you can't get Perfect
  const [peekVisible, setPeekVisible] = useState(false);
  const [peekedOnce, setPeekedOnce] = useState(false);
  const [errorCount, setErrorCount] = useState(0);
  const lastLenRef = useRef(0);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setInput('');
    setStatus('pending');
    setStartTime(null);
    setPeekVisible(false);
    setPeekedOnce(false);
    setErrorCount(0);
    lastLenRef.current = 0;
    // Autofocus on every sentence change so the next question is type-ready.
    requestAnimationFrame(() => inputRef.current?.focus());
  }, [sentence.id]);

  // Auto-play prompt + cancel on unmount/sentence-change so TTS doesn't leak.
  useEffect(() => {
    const t = setTimeout(() => speak(sentence.en), 350);
    return () => {
      clearTimeout(t);
      if ('speechSynthesis' in window) window.speechSynthesis.cancel();
    };
  }, [sentence.id, sentence.en]);

  function speak(text: string, rate = 0.95): void {
    if (!('speechSynthesis' in window)) return;
    window.speechSynthesis.cancel();
    const u = new SpeechSynthesisUtterance(text);
    u.lang = 'en-US';
    u.rate = rate;
    window.speechSynthesis.speak(u);
  }

  /** Strip punctuation + whitespace-normalize for tolerant comparison. */
  const normalize = (s: string): string =>
    s.toLowerCase().replace(/[^a-z' ]/g, ' ').replace(/\s+/g, ' ').trim();

  const targetLetters = useMemo(
    () => normalize(sentence.en).replace(/[^a-z']/g, ''),
    [sentence.en],
  );
  const inputLetters = useMemo(
    () => normalize(input).replace(/[^a-z']/g, ''),
    [input],
  );

  const correctSoFar = useMemo(
    () => inputLetters.split('').filter((c, i) => c === targetLetters[i]).length,
    [inputLetters, targetLetters],
  );
  const accuracy = inputLetters.length === 0 ? 100 : Math.round((correctSoFar / inputLetters.length) * 100);
  const wpm = useMemo(() => {
    if (startTime === null || inputLetters.length === 0) return 0;
    const mins = (Date.now() - startTime) / 60000;
    return mins <= 0 ? 0 : Math.round(inputLetters.length / 5 / mins);
  }, [startTime, inputLetters.length]);

  function handleChange(v: string): void {
    if (status !== 'pending') return;
    if (startTime === null && v.length > 0) setStartTime(Date.now());

    if (v.length > lastLenRef.current) {
      const newCh = v[v.length - 1] ?? '';
      const newInputLetters = normalize(v).replace(/[^a-z']/g, '');
      const idx = newInputLetters.length - 1;
      if (idx >= 0) {
        const expected = targetLetters[idx];
        if (expected !== undefined && newCh.toLowerCase() === expected) sfx.click();
        else if (/[a-zA-Z']/.test(newCh)) {
          sfx.miss();
          setErrorCount((c) => c + 1);
        }
      }
    }
    lastLenRef.current = v.length;
    setInput(v);
  }

  function submit(): void {
    if (status !== 'pending' || input.length === 0) return;
    const correct = normalize(input) === normalize(sentence.en);
    const elapsedMs = startTime !== null ? Date.now() - startTime : 0;
    const g = grade({
      correct, elapsedMs, hintUsed: peekedOnce, errorCount,
      fastThresholdMs: 4000 + targetLetters.length * 250,
    });
    submitGrade(g);
    if (correct) {
      setStatus('correct');
      setProgressBoost(true);
      markComplete();
      bumpStreak();
      if (g === 'perfect' || g === 'great') bumpMastery();
    } else {
      setStatus('wrong');
      recordMistake({ errorType: 'spelling', userInput: input });
    }
  }

  function reset(): void {
    setInput('');
    setStatus('pending');
    setStartTime(null);
    setPeekVisible(false);
    setPeekedOnce(false);
    setErrorCount(0);
    lastLenRef.current = 0;
    inputRef.current?.focus();
  }

  function togglePeek(): void {
    if (status !== 'pending') return;
    setPeekVisible((v) => {
      // First open marks the penalty; subsequent toggles don't undo it.
      if (!v) setPeekedOnce(true);
      return !v;
    });
  }

  return (
    <>
      <GameHud />
      <div className="space-y-8 scanlines relative pb-4">
        <StudyHeader
          courseId={course.id} courseTitle={course.title}
          lessonTitle={lesson.title} sentenceId={sentence.id}
          mode="typing"
          index={index} total={total} progressPct={progressPct}
        />

        <div className="flex gap-2 text-xs font-arcade">
          <StatChip label="WPM" value={wpm.toString()} tone="cyan" />
          <StatChip label="ACC" value={`${accuracy}%`} tone="lime" />
          <StatChip label="ERR" value={errorCount.toString()} tone={errorCount > 0 ? 'pink' : 'cream'} />
        </div>

        <GlassCard>
          <div className="text-xs uppercase tracking-[0.18em] text-honey-300 font-arcade mb-3">
            LISTEN · TYPE WHAT YOU HEAR
          </div>
          <div className="text-lg md:text-2xl text-cream-100 mb-8 text-balance leading-relaxed">
            {sentence.zh}
          </div>

          <div className="flex flex-col items-center mb-8">
            <button
              onClick={() => speak(sentence.en)}
              aria-label="重新播放"
              className="group relative h-20 w-20 rounded-full bg-gradient-to-br from-honey-400 to-ember-500 text-ink-950 grid place-items-center shadow-[0_8px_28px_-6px_rgba(0,240,255,0.55)] hover:scale-105 active:scale-95 transition-transform"
            >
              <Icon name="listen" size={32} strokeWidth={2.4} />
              <span className="absolute -inset-1 rounded-full border border-honey-400/50 group-hover:scale-110 transition-transform" />
            </button>
            <div className="mt-3 text-xs text-cream-400 font-arcade tracking-widest">TAP TO REPLAY</div>
            <button
              onClick={() => speak(sentence.en, 0.65)}
              className="mt-1.5 text-[11px] text-cream-500 hover:text-honey-300 transition-colors inline-flex items-center gap-1"
              aria-label="慢速重听"
            >
              <Icon name="slow" size="sm" /> 慢速重听
            </button>
          </div>

          <Skeleton target={sentence.en} input={input} status={status} peeked={peekVisible} />

          <div className="mt-5">
            <input
              ref={inputRef}
              type="text"
              value={input}
              onChange={(e) => handleChange(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && status === 'pending') submit(); }}
              disabled={status !== 'pending'}
              autoComplete="off"
              autoCapitalize="off"
              spellCheck={false}
              placeholder="只敲字母和空格 · 标点免敲 · 回车提交"
              className="w-full rounded-2xl bg-ink-800/60 border-2 border-honey-400/30 px-5 py-3 text-white font-mono focus:outline-none focus:border-honey-400/80 focus:bg-ink-800/80 transition-all"
              aria-label="输入英文"
            />
          </div>

          <div className="mt-5 flex items-center justify-between gap-3 flex-wrap text-xs font-arcade">
            <button
              onClick={togglePeek}
              disabled={status !== 'pending'}
              className="text-cream-400 hover:text-honey-300 disabled:opacity-40 inline-flex items-center gap-1.5"
            >
              <Icon name="peek" size="sm" />
              {peekVisible ? 'HIDE · 收起答案' : 'PEEK · 偷看答案'}
              {peekedOnce && !peekVisible && (
                <span className="text-ember-300/70 ml-1">已扣 PERFECT</span>
              )}
            </button>
            <div className="flex gap-2">
              {status === 'pending' && (
                <GlassButton onClick={submit} size="md" variant="solid" disabled={input.length === 0}>
                  SUBMIT ✓
                </GlassButton>
              )}
              {status !== 'pending' && <GlassButton onClick={reset} size="md">↺ RETRY</GlassButton>}
              {status === 'correct' && (
                <GlassButton onClick={next} size="md" variant="solid">
                  {index + 1 < total ? 'NEXT →' : 'COMPLETE ✨'}
                </GlassButton>
              )}
            </div>
          </div>
        </GlassCard>

        <AnimatePresence mode="wait">
          {status !== 'pending' && (
            <motion.div
              key={`${status}-${sentence.id}`}
              initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
              className={`rounded-3xl p-5 border ${status === 'correct'
                ? 'bg-mint-500/[0.08] border-mint-500/30'
                : 'bg-ember-500/[0.08] border-ember-500/30'}`}
            >
              <div className="text-xs font-arcade tracking-widest mb-3 inline-flex items-center gap-1.5" style={{ color: status === 'correct' ? '#aaff00' : '#ff2e88' }}>
                <Icon name={status === 'correct' ? 'check' : 'warn'} size="sm" />
                {status === 'correct' ? 'CORRECT · 标点已自动忽略' : 'MISS · 看答案对照'}
              </div>
              <div className="grid gap-2 text-sm">
                <div>
                  <span className="text-cream-400">你的：</span>
                  <span className={`font-mono ${status === 'correct' ? 'text-mint-200' : 'text-ember-200'}`}>{input || '(空)'}</span>
                </div>
                <div>
                  <span className="text-cream-400">正确：</span>
                  <span className="font-mono text-mint-200">
                    <TapToAsk sentence={sentence.en} tone="lime" />
                  </span>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        <ModeSwitcher lessonId={lesson.id} current="typing" />
      </div>
    </>
  );
}

/** Word-shape skeleton: letters→underscores that fill in as user types. */
function Skeleton({
  target, input, status, peeked,
}: {
  target: string;
  input: string;
  status: 'pending' | 'correct' | 'wrong';
  peeked: boolean;
}): JSX.Element {
  const inputLetters = input.toLowerCase().replace(/[^a-z']/g, '');
  const showAll = status !== 'pending' || peeked;
  let letterIdx = 0;
  const chars = target.split('');

  return (
    <div className="font-mono text-2xl md:text-3xl leading-relaxed tracking-wider break-words select-none mb-2 px-1">
      {chars.map((ch, i) => {
        if (ch === ' ') return <span key={`s-${i}`} className="inline-block w-3" aria-hidden />;
        const isLetter = /[a-zA-Z']/.test(ch);
        if (!isLetter) {
          return <span key={`p-${i}`} className="inline-block text-cream-500/60 mx-0.5" title="标点免敲">{ch}</span>;
        }
        const myIdx = letterIdx++;
        const typed = inputLetters[myIdx];
        if (showAll) {
          if (typed === undefined) return <span key={`l-${i}`} className="inline-block text-honey-300/70">{ch}</span>;
          const ok = typed === ch.toLowerCase();
          return (
            <span key={`l-${i}`} className={ok ? 'inline-block text-mint-300' : 'inline-block text-ember-400 underline decoration-wavy'}>
              {ch}
            </span>
          );
        }
        if (typed === undefined) {
          return <span key={`l-${i}`} className="inline-block min-w-[0.65em] text-center text-cream-500/40 border-b-2 border-honey-400/15">_</span>;
        }
        const ok = typed === ch.toLowerCase();
        return (
          <span key={`l-${i}`} className={`inline-block min-w-[0.65em] text-center ${ok ? 'text-mint-300' : 'text-ember-400 underline decoration-wavy'}`}>
            {ok ? ch : typed}
          </span>
        );
      })}
    </div>
  );
}
